import type { Granularity, RowValue, SchemaColumn } from "./api";

const numberFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat("id-ID", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const shortDateFormatter = new Intl.DateTimeFormat("id-ID", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const monthFormatter = new Intl.DateTimeFormat("id-ID", {
  month: "long",
  year: "numeric",
});

export const EMPTY_CELL = "—";

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return EMPTY_CELL;
  }
  return numberFormatter.format(value);
}

/** Parses `YYYY-MM-DD` as a local date (avoids the UTC off-by-one shift). */
export function parseDateLike(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

export function daysAgoIso(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toIsoDate(date);
}

export function startOfMonthIso(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

export function formatDate(value: RowValue): string {
  if (value === null || value === undefined || value === "") {
    return EMPTY_CELL;
  }
  const raw = String(value);
  const date = parseDateLike(raw);
  if (date) {
    return dateFormatter.format(date);
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? raw : dateFormatter.format(parsed);
}

export function formatCell(value: RowValue, column?: SchemaColumn): string {
  if (value === null || value === undefined || value === "") {
    return EMPTY_CELL;
  }
  if (typeof value === "boolean") {
    return value ? "Ya" : "Tidak";
  }
  if (column?.type === "number") {
    const numeric = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numeric) ? numberFormatter.format(numeric) : String(value);
  }
  if (column?.type === "date") {
    return formatDate(value);
  }
  return String(value);
}

const MONTH_NAMES = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

/** Human label for a report bucket key, tolerant of the upstream's key format. */
export function bucketLabel(granularity: Granularity, key: string): string {
  const raw = key.trim();

  if (granularity === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(raw);
    if (match) {
      const monthIndex = Number(match[2]) - 1;
      const name = MONTH_NAMES[monthIndex];
      return name ? `${name} ${match[1]}` : raw;
    }
    const date = parseDateLike(raw);
    return date ? monthFormatter.format(date) : raw;
  }

  if (granularity === "weekly") {
    const match = /^(\d{4})-?W(\d{1,2})$/i.exec(raw);
    if (match) {
      return `Minggu ${Number(match[2])} · ${match[1]}`;
    }
    const date = parseDateLike(raw);
    if (date) {
      const end = new Date(date);
      end.setDate(end.getDate() + 6);
      return `${shortDateFormatter.format(date)} – ${shortDateFormatter.format(end)}`;
    }
    return raw;
  }

  const date = parseDateLike(raw);
  return date ? shortDateFormatter.format(date) : raw;
}

export function bucketLabelFromBucket(
  granularity: Granularity,
  bucket: { key: string; label?: string },
): string {
  const label = bucket.label?.trim();
  if (label && label !== bucket.key) {
    return label;
  }
  return bucketLabel(granularity, bucket.key);
}

export function isNumericColumn(column: SchemaColumn): boolean {
  return column.type === "number" || column.type === "formula";
}

export function isCategoricalColumn(column: SchemaColumn): boolean {
  return (
    column.type === "text" ||
    column.type === "select" ||
    column.type === "date" ||
    column.type === "time"
  );
}
